// FIXME: Missing error handling

import React, { useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';

import _ from 'lodash';
import axios from 'axios';
import Papa from 'papaparse';

import { useApp } from '../../_libs/hooks/useApp';
import { useMicrosoft } from '../../_libs/hooks/useMicrosoft';

import Message from '../../atoms/Message';
import MicrosoftSSOWidget from '../../molecules/MicrosoftSSOWidget';
import StandardPageTemplate from '../../templates/StandardPage';

const MAX_ROWS = 1000;

function parseCsv (
  file: File,
  userId: number,
  cb: (campaignId: number) => void,
  errorCb: (message: string) => void
) {
  Papa.parse(file, {
    header: true,
    transformHeader: (col) => { return col.trim(); },
    complete: (parseResults) => {
      // Create a Campaign to associate with the Leads
      axios.post('/create_campaign/', {
        userId: userId,
        name: file.name,
      })
        .then((results) => {
          const data = results.data;
          if (data.status !== 'OK') {
            errorCb(data.message);
          } else {
            const csvData = parseResults.data;
            if (csvData.length < 2) {
              errorCb('This list is too short. It must contain a header and at least one row of data.');
            } else if (csvData.length > MAX_ROWS) {
              errorCb(`The exceeds the maximum limit of ${MAX_ROWS.toLocaleString('en-US')} rows.`);
            }
            // Check header is included and has required data
            const header = Object.keys(csvData[0]);
            const requiredHeaders = ['organizationName', 'zipCode'];
            if (_.intersection(header, requiredHeaders).length !== requiredHeaders.length) {
              errorCb('Header must contain `organizationName`, and `zipCode` columns.');
            }
            const leads = csvData;
            axios
              .post(
                '/add_leads/',
                {
                  campaignId: data.campaign.id,
                  userId,
                  leads,
                }
              )
              .then((results) => {
                const data = results.data;
                if (data.status !== 'OK') {
                  errorCb(data.message);
                } else {
                  cb(data.campaignId);
                  errorCb(null);
                }
              });
          }
        });
    }
  });
}

const UploadCsvPage = (): JSX.Element => {
  const {
    userId,
  } = useApp();
  const { 
    error,
    graphData
  } = useMicrosoft();
  const {
    register,
    formState: { errors },
    handleSubmit,
    setError,
    watch,
  } = useForm();
  const [file, setFile] = useState<File>(null);
  const [campaignId, setCampaignId] = useState(-1);
  const [errorMessage, setErrorMessage] = useState<string>(null);

  function handleFileChange (files: FileList) {
    const file: File = files[0];
    if (file === null) {
      setError('file', {
        type: 'custom',
        message: 'Please select a file to upload.',
      });
    } else {
      setFile(file);
    }
  }

  function onSubmit (data: FieldValues) {
    if (data.file.length) {
      const file: File = data.file[0];
      const fileTypeMatch = file.name.toLowerCase().match(/^.*\.csv$/);
      if (fileTypeMatch.length === 0) {
        setError('file', {
          type: 'custom',
          message: `File type ${fileTypeMatch[1]} is not supported. Please select a .csv.`,
        });
      }
      parseCsv(file, userId, setCampaignId, setErrorMessage);
    }
  }

  watch ((data, { name, type }) => {
    if (name === 'file' && type === 'change' && data.file.length === 1) {
      handleFileChange(data.file as FileList);
    }
  });

  if (!graphData) {
    return (
      <StandardPageTemplate>
        <MicrosoftSSOWidget error={error} type={'subscriber'} />
      </StandardPageTemplate>
    );
  } else {
    return (
      <StandardPageTemplate>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div>
            <h1 className="f-serif f-normal">
              Upload a CSV to Generate Reports
            </h1>
            <p>A maximum of {MAX_ROWS.toLocaleString('en-US')} rows is allowed. Columns name 'organizationName' and 'zipCode' are required.</p>
            <p className="f-small">Use `logoUrl` column for logo file names.</p>
          </div>
          <label className="button f-sans inline-block mb-0 mr-2">
            Choose a File to Upload
            <input
              className="inline-block"
              accept=".csv"
              type="file" {
                ...register(
                  'file',
                  {
                    required: 'A .csv file is required.',
                  }
                )
              }
              />
          </label>
          { file && <span className="f-medium f-italic">../{file.name}</span>}
          { errors.file && <p>{errors.file.message.toString()}</p>}
          <button
            className="block"
            type="submit"
            disabled={!file}>
              Upload
          </button>
        </form>
        {
          errorMessage
            &&
              <Message type="error">
                {errorMessage}
              </Message>
        }
        {
          !errorMessage && campaignId !== -1
            && <Link className="button td-none" to={`/microsoft/bulk_upload/campaigns/${campaignId}/`}>View Campaign</Link>
        }
      </StandardPageTemplate>
    );
  }
};

export default UploadCsvPage;
